import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from '../../config';
import { useCookies } from "react-cookie";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  HStack,
  Text,
  Checkbox,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FiLock, FiUser } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import { colors, gradients, radii, shadows } from "./designSystem";

const StaffLoginForm = ({
  onSuccess,
  onCancel,
  showLinks = true,
  showToast = true,
  mode = "page",
  redirectPath = null,
}) => {
  const [, setCookie] = useCookies(["role"]);
  const [inputValue, setInputValue] = useState({ username: "", password: "" });
  const { username, password } = inputValue;

  const syncPendingPreferredPharmacy = async (role) => {
    if (role !== "patient") return;
    const pending = localStorage.getItem("pendingPreferredPharmacy");
    if (!pending) return;
    try {
      const payload = JSON.parse(pending);
      if (!payload?.pharmacyId) return;
      await axios.post(
        `${API_BASE}/patient/favorite-pharmacies`,
        { ...payload, isPreferred: true },
        { withCredentials: true }
      );
      localStorage.removeItem("pendingPreferredPharmacy");
    } catch (err) {
      // Keep pending preference for next login
    }
  };

  const handleOnChange = (e) => {
    setInputValue({ ...inputValue, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    try {
      const { data } = await axios.post(
        `${API_BASE}/login`,
        { username, password },
        { withCredentials: true }
      );
      const { success, message, role } = data;
      if (success) {
        if (role) setCookie("role", role, { path: "/" });
        await syncPendingPreferredPharmacy(role);
        const resolveTarget = () => {
          if (redirectPath) return redirectPath;
          if (role === "patient") return "/home";
          if (role === "pharmacist") return "/pharmacist/home";
          if (role === "admin") return "/admin-home";
          return "/home";
        };
        if (showToast) toast.success(message, { position: "bottom-left" });
        if (onSuccess) {
          onSuccess({ role });
        } else {
          setTimeout(() => {
            window.location.replace(resolveTarget());
          }, 100);
        }
      } else {
        if (showToast) toast.error(message, { position: "bottom-left" });
      }
    } catch (err) {
      if (showToast) toast.error("Login failed", { position: "bottom-left" });
    }
  };

  return (
    <Box>
      <Stack spacing="5" as="form" onSubmit={handleSubmit}>
        <FormControl>
          <FormLabel htmlFor="username" fontSize="13px" fontWeight="800" color={colors.neutral[700]}>
            Username
          </FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none" color={colors.neutral[400]}>
              <FiUser />
            </InputLeftElement>
            <Input
              id="username"
              name="username"
              type="text"
              value={username}
              onChange={handleOnChange}
              placeholder="Enter your username"
              bg={colors.neutral[50]}
              borderColor={colors.neutral[200]}
              borderRadius={radii.md}
              h="48px"
              _hover={{ borderColor: colors.brand[200] }}
              _focusVisible={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
            />
          </InputGroup>
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="password" fontSize="13px" fontWeight="800" color={colors.neutral[700]}>
            Password
          </FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none" color={colors.neutral[400]}>
              <FiLock />
            </InputLeftElement>
            <Input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={handleOnChange}
              placeholder="Enter your password"
              bg={colors.neutral[50]}
              borderColor={colors.neutral[200]}
              borderRadius={radii.md}
              h="48px"
              _hover={{ borderColor: colors.brand[200] }}
              _focusVisible={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
            />
          </InputGroup>
        </FormControl>
        <HStack justify="space-between">
          <Checkbox defaultChecked colorScheme="teal" color={colors.neutral[600]}>
            Remember me
          </Checkbox>
          <Button
            as={Link}
            to="/forgot-password"
            variant="ghost"
            size="sm"
            color={colors.brand[700]}
            fontWeight="800"
            px={2}
          >
            Forgot password?
          </Button>
        </HStack>
        <Stack spacing="4">
          <Button
            bgGradient={gradients.brand}
            color="white"
            type="submit"
            h="50px"
            borderRadius={radii.md}
            fontWeight="800"
            boxShadow={shadows.brand}
            _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover, transform: "translateY(-1px)" }}
          >
            Sign in
          </Button>
          {showLinks && (
            <>
              <HStack spacing={3}>
                <Divider borderColor={colors.neutral[200]} />
                <Text fontSize="12px" whiteSpace="nowrap" color={colors.neutral[500]} fontWeight="800" letterSpacing="0.08em" textTransform="uppercase">
                  Patient access
                </Text>
                <Divider borderColor={colors.neutral[200]} />
              </HStack>
              <Text fontSize="13px" color={colors.neutral[500]} lineHeight="1.7" m={0}>
                Patients can log in with their phone number and OTP from the home page.
              </Text>
              <Button
                as={Link}
                to="/home"
                variant="outline"
                borderColor={colors.brand[200]}
                color={colors.brand[700]}
                fontWeight="800"
                borderRadius={radii.md}
                _hover={{ bg: colors.brand[50] }}
              >
                Back to home
              </Button>
            </>
          )}
          {mode === "modal" && onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
      {showToast && <ToastContainer />}
    </Box>
  );
};

export default StaffLoginForm;
